/**
 * DemandIntakeForm — 3-step modal for creating a new demand. Mockup §5718-5961.
 *
 * Steps:
 *   1. Required: title, issue_problem, target_user, focus_area
 *   2. Helpful:  background, anticipated_benefit, business_criticality, department
 *   3. Resourcing: skills (chip list), hours_per_week, duration, requirements
 *
 * The footer used to show a weighted "intake quality" score. It was one of
 * #998's three competing definitions of completeness, and the last one left:
 * the readiness report (#1207) answers the same question per stage, against
 * the rules that actually gate the next move. Removed 2026-09-22.
 *
 * Substrate: useCreateDemand() (src/hooks/useDemands.ts).
 */

import { useEffect, useMemo, useState } from 'react'
import {
  Button,
  Spinner,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import {
  ArrowLeft16Regular,
  ArrowRight16Regular,
  Checkmark12Filled,
  Dismiss16Regular,
} from '@fluentui/react-icons'
import { useCreateDemand } from '../hooks/useDemands'
import { SkillTypeaheadChipList } from './SkillTypeaheadChipList'
import type { DemandCreateInput, DemandType, SupportMode } from '../types/demand'
import { useProducts } from '../hooks/useProducts'
import { FOCUS_AREAS } from '../types/focus_area'
import { apiErrorMessage } from '../lib/apiError'
import { useTestDataVisibility } from '../context/TestDataVisibilityContext'
import { useEffectiveUser } from '../hooks/useEffectiveUser'
import { bayerColors } from '../theme'

// ─── Form state ──────────────────────────────────────────────────────────────

export interface IntakeFields {
  title: string
  issue_problem: string
  target_user: string
  focus_area: string
  background: string
  anticipated_benefit: string
  business_criticality: string
  department: string
  skills: string[]
  hours_per_week: string
  duration: string
  requirements: string
  demand_type: DemandType
  support_mode: SupportMode
  linked_product_id: number | null
  linked_freehand: string
}

const EMPTY_FIELDS: IntakeFields = {
  title: '',
  issue_problem: '',
  target_user: '',
  focus_area: '',
  background: '',
  anticipated_benefit: '',
  business_criticality: '',
  department: '',
  skills: [],
  hours_per_week: '',
  duration: '',
  requirements: '',
  demand_type: 'new_demand',
  support_mode: 'council_supported',
  linked_product_id: null,
  linked_freehand: '',
}

/**
 * Support mode, as two visible options.
 *
 * Order matters: council-supported is genuinely the common case, so it comes
 * first and stays the default in EMPTY_FIELDS. The point of showing both is not
 * to make people choose — it is to make the minority case findable by someone
 * who is in it.
 *
 * Each description names the CONSEQUENCE rather than restating the label,
 * because that is what tells a requestor which one they are. "Self-supported"
 * means nothing on its own; "you already have the people" is recognisable.
 */
/**
 * Demand type, as two visible options (#1030).
 *
 * Same shape as SUPPORT_MODE_CHOICES below, and the same rule for the
 * descriptions: name the CONSEQUENCE, not a restatement of the label. "Change
 * request" is only meaningful once you know it wants a product to point at.
 */
const DEMAND_TYPE_CHOICES: { value: DemandType; label: string; description: string }[] = [
  {
    value: 'new_demand',
    label: 'New demand',
    description: 'Something that does not exist yet.',
  },
  {
    value: 'change_request',
    label: 'Change request',
    description: 'A change to a product that already exists — you will pick which one next.',
  },
]

const SUPPORT_MODE_CHOICES: { value: SupportMode; label: string; description: string }[] = [
  {
    value: 'council_supported',
    label: 'Council-supported',
    description: 'The council finds and staffs people for this.',
  },
  {
    value: 'self_supported',
    label: 'Self-supported',
    description: 'You already have the people. The council tracks it but does not staff it.',
  },
]

// ─── Pure helpers (exported for testability) ─────────────────────────────────

/** Step-1 required-field gate per mockup §5714-5716. */
export function step1Valid(f: IntakeFields): boolean {
  return Boolean(
    f.title.trim() && f.issue_problem.trim() && f.target_user.trim() && f.focus_area,
  )
}

/**
 * Has the user entered anything worth confirming before we discard it?
 * `focus_area` is auto-prefilled from the council member's own FA when the
 * dialog opens, so it only counts as user input when changed away from `autoFa`.
 */
export function isIntakeDirty(f: IntakeFields, autoFa: string): boolean {
  if (f.skills.length > 0) return true
  if (f.focus_area && f.focus_area !== autoFa) return true
  const textFields = [
    f.title,
    f.issue_problem,
    f.target_user,
    f.background,
    f.anticipated_benefit,
    f.business_criticality,
    f.department,
    f.hours_per_week,
    f.duration,
    f.requirements,
  ]
  return textFields.some((v) => String(v ?? '').trim() !== '')
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 40, 71, 0.5)',
    backdropFilter: 'blur(3px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 998,
    padding: '24px',
  },
  // Discard confirmation renders INSIDE the overlay (same stacking context),
  // not as a portaled Fluent Dialog — a portaled dialog lands under this
  // z-index:998 overlay and its buttons become unclickable (the "Keep editing"
  // data-loss bug). Absolute + inset:0 covers the viewport; zIndex:1 sits it
  // above the card.
  confirmScrim: {
    position: 'absolute',
    inset: 0,
    background: 'rgba(15, 40, 71, 0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    zIndex: 1,
  },
  confirmCard: {
    background: '#FFFFFF',
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadow28,
    width: '100%',
    maxWidth: '420px',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  confirmTitle: {
    fontSize: '18px',
    fontWeight: 600,
    margin: 0,
    color: tokens.colorNeutralForeground1,
  },
  confirmBody: {
    fontSize: '14px',
    margin: 0,
    color: tokens.colorNeutralForeground2,
    lineHeight: 1.5,
  },
  confirmActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    marginTop: '8px',
  },
  card: {
    background: '#FFFFFF',
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadow28,
    width: '100%',
    maxWidth: '720px',
    maxHeight: 'calc(100vh - 64px)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  head: {
    padding: '20px 24px 12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
  },
  headTitle: {
    fontSize: '20px',
    fontWeight: 600,
    margin: 0,
    lineHeight: 1.3,
    color: tokens.colorNeutralForeground1,
  },
  headSub: {
    color: tokens.colorNeutralForeground3,
    fontSize: '13px',
    marginTop: '2px',
  },
  testBadge: {
    marginTop: '8px',
    padding: '6px 10px',
    borderRadius: tokens.borderRadiusMedium,
    background: '#FFF4CE',
    border: '1px solid #F2D98E',
    color: '#6b5400',
    fontSize: '12px',
    fontWeight: 500,
  },
  stepper: {
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
    marginTop: '12px',
    flexWrap: 'wrap',
  },
  stepPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    borderRadius: '999px',
    fontSize: '12px',
    fontWeight: 600,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    color: tokens.colorNeutralForeground3,
    background: '#FAFAFA',
  },
  stepPillActive: {
    background: bayerColors.navyMid,
    color: '#FFFFFF',
    border: `1px solid ${bayerColors.navyMid}`,
  },
  stepPillDone: {
    background: '#F1FAF1',
    color: '#2e7d32',
    border: '1px solid #9FD89F',
  },
  stepPillNum: {
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    background: tokens.colorNeutralStroke2,
    color: tokens.colorNeutralForeground3,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '9px',
  },
  stepPillNumActive: {
    background: 'rgba(255,255,255,.25)',
    color: '#FFFFFF',
  },
  stepPillNumDone: {
    background: '#1b5e20',
    color: '#FFFFFF',
  },
  body: {
    padding: '20px 24px',
    overflowY: 'auto',
    flex: 1,
  },
  bodyIntro: {
    margin: '0 0 16px',
    color: tokens.colorNeutralForeground2,
    fontSize: '13px',
  },
  field: {
    marginBottom: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  fieldRow2: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  fieldLabel: {
    fontSize: '14px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  fieldRequired: {
    color: '#c62828',
    marginLeft: '2px',
  },
  fieldOptional: {
    color: tokens.colorNeutralForeground3,
    fontWeight: 400,
    fontSize: '12px',
    marginLeft: '4px',
  },
  fieldHint: {
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
    fontWeight: 400,
  },
  input: {
    fontFamily: 'inherit',
    fontSize: '14px',
    padding: '8px 12px',
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderBottom: `1px solid ${tokens.colorNeutralStrokeAccessible}`,
    background: '#FFFFFF',
    outline: 'none',
    color: tokens.colorNeutralForeground1,
    ':focus': {
      border: `1px solid ${bayerColors.navyMid}`,
      borderBottom: `2px solid ${bayerColors.navyMid}`,
    },
  },
  textarea: {
    fontFamily: 'inherit',
    fontSize: '14px',
    padding: '8px 12px',
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderBottom: `1px solid ${tokens.colorNeutralStrokeAccessible}`,
    background: '#FFFFFF',
    outline: 'none',
    resize: 'vertical',
    color: tokens.colorNeutralForeground1,
    ':focus': {
      border: `1px solid ${bayerColors.navyMid}`,
      borderBottom: `2px solid ${bayerColors.navyMid}`,
    },
  },
  // A <select> collapses to the selected value, so the alternative is only
  // discoverable by opening it. For support mode that hid a choice with
  // downstream consequences (see the field for what). Both options stay on
  // screen instead — the common one is still pre-selected, so the majority
  // case costs no extra click.
  choiceGroup: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px',
    '@media (max-width: 640px)': {
      gridTemplateColumns: '1fr',
    },
  },
  choice: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '10px 12px',
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    background: '#FFFFFF',
    cursor: 'pointer',
    transitionProperty: 'border-color, background',
    transitionDuration: '120ms',
    // Longhands: griffel rejects the `borderColor` shorthand wherever `border`
    // is also set. Same reason TalentDirectory.tsx spells all four out.
    ':hover': {
      borderTopColor: bayerColors.navyMid,
      borderRightColor: bayerColors.navyMid,
      borderBottomColor: bayerColors.navyMid,
      borderLeftColor: bayerColors.navyMid,
    },
    ':focus-within': {
      borderTopColor: bayerColors.navyMid,
      borderRightColor: bayerColors.navyMid,
      borderBottomColor: bayerColors.navyMid,
      borderLeftColor: bayerColors.navyMid,
      boxShadow: `0 0 0 1px ${bayerColors.navyMid}`,
    },
  },
  choiceSelected: {
    borderTopColor: bayerColors.navyMid,
    borderRightColor: bayerColors.navyMid,
    borderBottomColor: bayerColors.navyMid,
    borderLeftColor: bayerColors.navyMid,
    background: bayerColors.navyLight,
  },
  choiceRadio: {
    marginTop: '2px',
    accentColor: bayerColors.navyMid,
    flexShrink: 0,
  },
  choiceText: { display: 'flex', flexDirection: 'column', gap: '2px' },
  choiceTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  choiceDesc: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    lineHeight: 1.35,
  },
  select: {
    fontFamily: 'inherit',
    fontSize: '14px',
    padding: '8px 12px',
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderBottom: `1px solid ${tokens.colorNeutralStrokeAccessible}`,
    background: '#FFFFFF',
    outline: 'none',
    color: tokens.colorNeutralForeground1,
    ':focus': {
      border: `1px solid ${bayerColors.navyMid}`,
      borderBottom: `2px solid ${bayerColors.navyMid}`,
    },
  },
  charCounter: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    marginTop: '4px',
  },
  skillWrap: {
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderBottom: `1px solid ${tokens.colorNeutralStrokeAccessible}`,
    borderRadius: tokens.borderRadiusMedium,
    background: '#FFFFFF',
    padding: '6px',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    alignItems: 'center',
    minHeight: '38px',
    ':focus-within': {
      border: `1px solid ${bayerColors.navyMid}`,
      borderBottom: `2px solid ${bayerColors.navyMid}`,
    },
  },
  foot: {
    padding: '12px 24px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    background: '#FAFAFA',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  blockers: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteDarkOrangeForeground1,
    background: tokens.colorNeutralBackground3,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  errorBanner: {
    margin: '0 0 16px',
    padding: '8px 12px',
    background: '#FDE7E9',
    color: '#b10e1c',
    border: '1px solid #f5c6c6',
    borderRadius: tokens.borderRadiusMedium,
    fontSize: '13px',
  },
})

// ─── Step renderers ──────────────────────────────────────────────────────────

interface StepProps {
  fields: IntakeFields
  update: <K extends keyof IntakeFields>(key: K, value: IntakeFields[K]) => void
  userFa?: string
  productOptions?: { id: number; label: string }[]
}

function Step1Required({ fields, update, userFa, productOptions = [] }: StepProps) {
  const styles = useStyles()
  const descLen = fields.issue_problem.length
  const descTier =
    descLen >= 200 ? '· best' :
    descLen >= 100 ? '· good' :
    descLen >= 50 ? '· ok' :
    '· too short'

  const isCrossFa = !!(userFa && fields.focus_area && fields.focus_area !== userFa)

  return (
    <>
      <p className={styles.bodyIntro}>
        Tell us about the problem. These four fields are required before the council can review.
      </p>

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="intake-focus">
          Focus area <span className={styles.fieldRequired}>*</span>
        </label>
        <select
          id="intake-focus"
          className={styles.select}
          value={fields.focus_area}
          onChange={(e) => update('focus_area', e.target.value)}
        >
          <option value="">Select a focus area…</option>
          {FOCUS_AREAS.map((fa) => (
            <option key={fa.code} value={fa.code}>
              {fa.code} — {fa.name}
            </option>
          ))}
        </select>
        {isCrossFa && (
          <span className={styles.fieldHint} style={{ color: '#d97706', marginTop: 4 }}>
            ⚠ You are creating a demand in another focus area ({fields.focus_area}), not your own ({userFa}).
          </span>
        )}
      </div>

      {/*
        Two visible options, same treatment support mode got in v2.8.42 and for
        the same reason: a <select> renders as one line showing the selected
        value, so "Demand type: New demand" reads as a statement about the
        demand rather than a question, and the alternative exists only for
        someone who thinks to open it.

        Still not a forced choice. New demand is the overwhelming majority, so
        it stays pre-selected and costs nobody a click. What changes is that
        somebody filing a change request can see that is a thing to be.

        Milder than the support-mode case, which is why it waited: picking
        "Change request" reveals the product picker below, and you cannot link
        a product without coming through here — so this one partly
        self-corrects, and the default is the safe answer for anyone who does
        not engage with it. See #1030.
      */}
      <div className={styles.field}>
        <span className={styles.fieldLabel} id="intake-demand-type-label">
          Demand type <span className={styles.fieldRequired}>*</span>
        </span>
        <div
          className={styles.choiceGroup}
          role="radiogroup"
          aria-labelledby="intake-demand-type-label"
        >
          {DEMAND_TYPE_CHOICES.map((choice) => (
            <label
              key={choice.value}
              className={mergeClasses(
                styles.choice,
                fields.demand_type === choice.value && styles.choiceSelected,
              )}
              data-testid={`intake-demand-type-${choice.value}`}
            >
              <input
                type="radio"
                name="intake-demand-type"
                className={styles.choiceRadio}
                value={choice.value}
                checked={fields.demand_type === choice.value}
                onChange={() => update('demand_type', choice.value)}
              />
              <span className={styles.choiceText}>
                <span className={styles.choiceTitle}>{choice.label}</span>
                <span className={styles.choiceDesc}>{choice.description}</span>
              </span>
            </label>
          ))}
        </div>
      </div>

      {fields.demand_type === 'change_request' && (
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="intake-linked-product">
            Existing product <span className={styles.fieldRequired}>*</span>
          </label>
          <span className={styles.fieldHint}>
            Pick the product this change targets. If it isn't listed, name it below.
          </span>
          <select
            id="intake-linked-product"
            className={styles.select}
            value={fields.linked_product_id ?? ''}
            onChange={(e) =>
              update('linked_product_id', e.target.value ? Number(e.target.value) : null)
            }
          >
            <option value="">— pick a product (or use freehand) —</option>
            {productOptions.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
          {fields.linked_product_id == null && (
            <input
              type="text"
              className={styles.input}
              style={{ marginTop: 6 }}
              value={fields.linked_freehand}
              onChange={(e) => update('linked_freehand', e.target.value)}
              placeholder="Freehand: name the product/demand this changes…"
            />
          )}
        </div>
      )}

      {/*
        Two visible options rather than a <select>, which showed only the
        selected one — so "Support mode: Council-supported" read as a stated
        fact about the demand rather than a question being answered, and the
        alternative existed only for anyone who thought to open the dropdown.
        Nobody on production had ever seen these controls before v2.8.38, and
        every row reads council-supported because that is the column default.

        Not a forced choice: council-supported IS the common case, so it stays
        pre-selected and the majority pays nothing. What changes is that the
        minority can see they are the minority.

        Worth seeing, because the setting outlives intake: `self_supported`
        suppresses the whole Resourcing staffing group in EXPECTATION_VARIANTS.
        Left on the default, a requestor resourcing the work themselves gets
        nagged for staffing fields for the life of the demand and has no way to
        find out why.
      */}
      <div className={styles.field}>
        <span className={styles.fieldLabel} id="intake-support-mode-label">
          Support mode
        </span>
        <span className={styles.fieldHint}>Who finds the people for this?</span>
        <div
          className={styles.choiceGroup}
          role="radiogroup"
          aria-labelledby="intake-support-mode-label"
        >
          {SUPPORT_MODE_CHOICES.map((choice) => (
            <label
              key={choice.value}
              className={mergeClasses(
                styles.choice,
                fields.support_mode === choice.value && styles.choiceSelected,
              )}
              data-testid={`intake-support-mode-${choice.value}`}
            >
              <input
                type="radio"
                name="intake-support-mode"
                className={styles.choiceRadio}
                value={choice.value}
                checked={fields.support_mode === choice.value}
                onChange={() => update('support_mode', choice.value)}
              />
              <span className={styles.choiceText}>
                <span className={styles.choiceTitle}>{choice.label}</span>
                <span className={styles.choiceDesc}>{choice.description}</span>
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="intake-title">
          Title <span className={styles.fieldRequired}>*</span>
        </label>
        <span className={styles.fieldHint}>
          A short, descriptive name. E.g., "Real-time AE signal detection across post-marketing data"
        </span>
        <input
          id="intake-title"
          type="text"
          className={styles.input}
          value={fields.title}
          onChange={(e) => update('title', e.target.value)}
          placeholder="Real-time adverse event signal detection..."
        />
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="intake-issue">
          What is the problem? <span className={styles.fieldRequired}>*</span>
        </label>
        <span className={styles.fieldHint}>
          Describe the issue, current process, and why it matters now. The more you write, the
          less the council has to ask.
        </span>
        <textarea
          id="intake-issue"
          rows={5}
          className={styles.textarea}
          value={fields.issue_problem}
          onChange={(e) => update('issue_problem', e.target.value)}
          placeholder="Current SAE signal review process takes 6-8 weeks per cycle because our team manually re-reads MedDRA-coded reports across three vendor systems..."
        />
        <div className={styles.charCounter} data-testid="intake-char-counter">
          {descLen} characters {descTier}
        </div>
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="intake-target-user">
          Target user <span className={styles.fieldRequired}>*</span>
        </label>
        <span className={styles.fieldHint}>Who will use what you're building?</span>
        <input
          id="intake-target-user"
          type="text"
          className={styles.input}
          value={fields.target_user}
          onChange={(e) => update('target_user', e.target.value)}
          placeholder="Pharmacovigilance review committee"
        />
      </div>
    </>
  )
}

function Step2Helpful({ fields, update }: StepProps) {
  const styles = useStyles()
  return (
    <>
      <p className={styles.bodyIntro}>
        Optional, but each field substantially helps council triage and prioritization.
      </p>

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="intake-background">
          Background <span className={styles.fieldOptional}>(optional)</span>
        </label>
        <span className={styles.fieldHint}>
          Context the council might need — prior attempts, current process, existing tooling.
        </span>
        <textarea
          id="intake-background"
          rows={3}
          className={styles.textarea}
          value={fields.background}
          onChange={(e) => update('background', e.target.value)}
          placeholder="Current quarterly review process documented in PV-SOP-2024-05. Three vendor systems: ArisGlobal, Veeva Vault Safety, internal AE database."
        />
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="intake-benefit">
          Expected benefit <span className={styles.fieldOptional}>(optional · +10%)</span>
        </label>
        <span className={styles.fieldHint}>What gets better if this ships? Quantify if you can.</span>
        <textarea
          id="intake-benefit"
          rows={2}
          className={styles.textarea}
          value={fields.anticipated_benefit}
          onChange={(e) => update('anticipated_benefit', e.target.value)}
          placeholder="Cut signal-detection lag from ~6 weeks to ~2 weeks. Catch class-effect signals before regulatory inquiry."
        />
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="intake-criticality">
          Business criticality <span className={styles.fieldOptional}>(optional · +10%)</span>
        </label>
        <span className={styles.fieldHint}>
          What's at risk if this doesn't happen? Regulatory exposure, missed opportunity, etc.
        </span>
        <textarea
          id="intake-criticality"
          rows={2}
          className={styles.textarea}
          value={fields.business_criticality}
          onChange={(e) => update('business_criticality', e.target.value)}
          placeholder="Regulatory exposure: missed last quarter's class signal flagged by EMA 3 weeks before our review."
        />
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="intake-department">
          Department / therapeutic area{' '}
          <span className={styles.fieldOptional}>(optional · +5%)</span>
        </label>
        <input
          id="intake-department"
          type="text"
          className={styles.input}
          value={fields.department}
          onChange={(e) => update('department', e.target.value)}
          placeholder="Pharmacovigilance Operations"
        />
      </div>
    </>
  )
}

function Step3Resourcing({ fields, update }: StepProps) {
  const styles = useStyles()

  return (
    <>
      <p className={styles.bodyIntro}>
        Resourcing details help talent decide whether to apply and let council match the right people.
      </p>

      <div className={styles.field}>
        <div className={styles.fieldLabel}>
          Required skills <span className={styles.fieldOptional}>(optional · +10%)</span>
        </div>
        <span className={styles.fieldHint}>
          From the skills catalog, so talent can be matched. Missing one? Report it
          through Help and feedback.
        </span>
        <div className={styles.skillWrap} data-testid="intake-skill-wrap">
          <SkillTypeaheadChipList
            values={fields.skills}
            onSave={(values) => update('skills', values)}
            ariaLabel="Required skills"
            addLabel="+ Add a required skill"
            placeholder="Search the skills catalog…"
          />
        </div>
      </div>

      <div className={styles.fieldRow2}>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="intake-hours">
            Hours per week <span className={styles.fieldOptional}>(optional · +10%)</span>
          </label>
          <input
            id="intake-hours"
            type="text"
            className={styles.input}
            value={fields.hours_per_week}
            onChange={(e) => update('hours_per_week', e.target.value)}
            placeholder="e.g. 10-20"
          />
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="intake-duration">
            Duration
          </label>
          <input
            id="intake-duration"
            type="text"
            className={styles.input}
            value={fields.duration}
            onChange={(e) => update('duration', e.target.value)}
            placeholder="e.g. 3 months"
          />
        </div>
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="intake-requirements">
          Other requirements <span className={styles.fieldOptional}>(optional)</span>
        </label>
        <span className={styles.fieldHint}>
          Tooling access, security clearance, location, etc.
        </span>
        <textarea
          id="intake-requirements"
          rows={2}
          className={styles.textarea}
          value={fields.requirements}
          onChange={(e) => update('requirements', e.target.value)}
          placeholder="MedDRA browser access; vendor data access agreements; security approval for cross-system data joining."
        />
      </div>
    </>
  )
}

// ─── Submit payload builder ──────────────────────────────────────────────────

/**
 * Build the DemandCreateInput payload from intake fields. Empty optional
 * fields are omitted so the backend can apply its own defaults.
 *
 * Note: backend requires `description`; the mockup uses `issue_problem` as
 * the primary problem-statement field, so we mirror it into both.
 */
export function buildCreatePayload(f: IntakeFields): DemandCreateInput {
  const payload: DemandCreateInput = {
    title: f.title.trim(),
    description: f.issue_problem.trim(),
    focus_area_code: f.focus_area,
    issue_problem: f.issue_problem.trim(),
  }
  if (f.target_user.trim()) payload.target_user = f.target_user.trim()
  if (f.background.trim()) payload.background = f.background.trim()
  if (f.anticipated_benefit.trim()) payload.anticipated_benefit = f.anticipated_benefit.trim()
  if (f.business_criticality.trim()) payload.business_criticality = f.business_criticality.trim()
  if (f.department.trim()) payload.department = f.department.trim()
  if (f.skills.length > 0) payload.skills = f.skills
  if (f.hours_per_week.trim()) {
    const n = Number(f.hours_per_week.trim())
    if (Number.isFinite(n) && n > 0) payload.hours_per_week = n
  }
  if (f.duration.trim()) payload.duration = f.duration.trim()
  if (f.requirements.trim()) payload.requirements = f.requirements.trim()
  payload.demand_type = f.demand_type
  payload.support_mode = f.support_mode
  if (f.demand_type === 'change_request') {
    if (f.linked_product_id != null) payload.linked_product_id = f.linked_product_id
    else if (f.linked_freehand.trim()) payload.linked_freehand = f.linked_freehand.trim()
  }
  return payload
}

/** A change request needs a link (a picked product OR a freehand note). */
export function classificationValid(f: IntakeFields): boolean {
  if (f.demand_type !== 'change_request') return true
  return f.linked_product_id != null || !!f.linked_freehand.trim()
}

/**
 * Which required things are still empty, by the label the user saw on the form.
 *
 * The submit button on step 3 is gated by `step1Valid && classificationValid` —
 * both step-1 conditions. So someone on the last step got a dead "Submit
 * demand" with nothing on screen naming a field two steps back, and the step-1
 * button's "Complete required fields" never said which field either. Worse,
 * `classificationValid` is a rule nobody can guess: a change request needs a
 * product link, and that is not one of the four fields marked required.
 *
 * A pure function rather than JSX so what the user is told is testable, and so
 * the button, the tooltip and the banner cannot drift into disagreeing about
 * what is missing. Order matches the form, so reading the list is a route
 * through the fields.
 */
export function missingRequired(f: IntakeFields): string[] {
  const missing: string[] = []
  if (!f.title.trim()) missing.push('Title')
  if (!f.issue_problem.trim()) missing.push('Problem statement')
  if (!f.target_user.trim()) missing.push('Target user')
  if (!f.focus_area) missing.push('Focus area')
  if (!classificationValid(f)) missing.push('Existing product this changes')
  return missing
}

// ─── Component ───────────────────────────────────────────────────────────────

export interface DemandIntakeFormProps {
  open: boolean
  onClose: () => void
  /** Called after the demand is created successfully (fires before close). */
  onSuccess?: (demandId: number) => void
}

export function DemandIntakeForm({ open, onClose, onSuccess }: DemandIntakeFormProps) {
  const styles = useStyles()
  const [step, setStep] = useState<1 | 2 | 3>(1)
  const [fields, setFields] = useState<IntakeFields>(EMPTY_FIELDS)
  // Products to offer when the demand is a change request (freehand fallback if
  // not listed). Only fetched while the form is open.
  const { data: productsResp } = useProducts(open ? {} : { enabled: false })
  const productOptions = useMemo(
    () => (productsResp?.items ?? []).map((p) => ({ id: p.id, label: p.name })),
    [productsResp],
  )
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)

  const { showTestData } = useTestDataVisibility()
  const effectiveUser = useEffectiveUser()
  const userFa = effectiveUser?.focus_area_code ?? ''
  const createMutation = useCreateDemand()

  // Pre-populate FA with council's own focus area when dialog opens.
  // Deliberately NOT keyed on fields.focus_area: this is a one-shot default
  // at open, not an enforcement — depending on it would re-fire the moment
  // the user clears the field and force their own FA back.
  useEffect(() => {
    if (open && userFa && !fields.focus_area) {
      setFields((prev) => ({ ...prev, focus_area: userFa }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userFa])

  const canAdvance = step1Valid(fields) && classificationValid(fields)
  // What is actually blocking, by the label on the form. Derived once so the
  // banner, the button label and the tooltip cannot disagree.
  const blockers = missingRequired(fields)

  function update<K extends keyof IntakeFields>(key: K, value: IntakeFields[K]) {
    setFields((prev) => ({ ...prev, [key]: value }))
  }

  function reset() {
    setStep(1)
    setFields(EMPTY_FIELDS)
    setSubmitError(null)
  }

  function handleClose() {
    setShowDiscardConfirm(false)
    reset()
    onClose()
  }

  const dirty = isIntakeDirty(fields, userFa)

  // Guard every dismiss path: if the user has typed anything, ask before
  // discarding — a stray backdrop click or Escape must not silently wipe a
  // multi-step form. Pristine forms close freely.
  function attemptClose() {
    if (dirty) setShowDiscardConfirm(true)
    else handleClose()
  }

  // Escape closes the form (through the same discard guard). While the discard
  // confirm is open, Escape dismisses just the confirm (= "keep editing") — the
  // confirm is now an in-overlay element, so we own its Escape here.
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return
      e.preventDefault()
      if (showDiscardConfirm) {
        setShowDiscardConfirm(false) // keep editing
        return
      }
      if (dirty) setShowDiscardConfirm(true)
      else handleClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, showDiscardConfirm, dirty])

  function handleNext() {
    if (step === 1 && !canAdvance) return
    if (step < 3) setStep((s) => (s === 1 ? 2 : 3))
  }

  function handleBack() {
    if (step > 1) setStep((s) => (s === 3 ? 2 : 1))
  }

  function handleSkip() {
    if (step === 2) setStep(3)
  }

  function handleSubmit() {
    if (!canAdvance) return
    setSubmitError(null)
    const payload = buildCreatePayload(fields)
    if (showTestData) payload.is_test = true
    createMutation.mutate(payload, {
      onSuccess: (demand) => {
        onSuccess?.(demand.id)
        reset()
        onClose()
      },
      onError: (err: unknown) => {
        setSubmitError(apiErrorMessage(err, 'Failed to submit demand. Please try again.'))
      },
    })
  }

  if (!open) return null

  const stepLabels: Array<{ num: 1 | 2 | 3; label: string }> = [
    { num: 1, label: 'Required' },
    { num: 2, label: 'Helpful' },
    { num: 3, label: 'Resourcing' },
  ]

  return (
    <div
      className={styles.overlay}
      onClick={attemptClose}
      role="presentation"
      data-testid="intake-overlay"
    >
      <div
        className={styles.card}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="intake-title-heading"
      >
        <header className={styles.head}>
          <div style={{ flex: 1 }}>
            <h2 id="intake-title-heading" className={styles.headTitle}>
              {showTestData ? 'New Test Demand' : 'New Demand'}
            </h2>
            <div className={styles.headSub}>
              Submit this to the council's triage queue. We'll check before discarding
              anything you've entered.
            </div>
            {showTestData && (
              <div className={styles.testBadge} data-testid="intake-test-badge">
                Test mode — this demand gets a <strong>[TEST]</strong> tag and won't email anyone.
              </div>
            )}
            <div className={styles.stepper} role="tablist" aria-label="Intake steps">
              {stepLabels.map((s) => {
                const isActive = step === s.num
                const isDone = step > s.num || (s.num === 1 && canAdvance && !isActive)
                const showCheck = isDone && !isActive
                return (
                  <span
                    key={s.num}
                    role="tab"
                    aria-selected={isActive}
                    data-testid={`step-pill-${s.num}`}
                    data-active={isActive || undefined}
                    data-done={isDone || undefined}
                    className={mergeClasses(
                      styles.stepPill,
                      isActive && styles.stepPillActive,
                      !isActive && isDone && styles.stepPillDone,
                    )}
                  >
                    <span
                      className={mergeClasses(
                        styles.stepPillNum,
                        isActive && styles.stepPillNumActive,
                        !isActive && isDone && styles.stepPillNumDone,
                      )}
                    >
                      {showCheck ? <Checkmark12Filled /> : s.num}
                    </span>
                    Step {s.num} · {s.label}
                  </span>
                )
              })}
            </div>
          </div>
          <Button
            appearance="subtle"
            icon={<Dismiss16Regular />}
            onClick={attemptClose}
            aria-label="Cancel"
          />
        </header>

        <div className={styles.body}>
          {submitError && (
            <div role="alert" className={styles.errorBanner} data-testid="intake-error">
              {submitError}
            </div>
          )}
          {step === 1 && (
            <Step1Required
              fields={fields}
              update={update}
              userFa={userFa}
              productOptions={productOptions}
            />
          )}
          {step === 2 && <Step2Helpful fields={fields} update={update} />}
          {step === 3 && (
            <Step3Resourcing fields={fields} update={update} />
          )}
        </div>

        {/* What is blocking, in words, on whichever step you are on.
            The submit button lives on step 3 but is gated by step-1 fields, so
            without this the last step showed a dead button and no reason —
            the single worst instance of a check that refuses without saying
            why. Named after the labels on the form so the list doubles as a
            route back to the fields. */}
        {blockers.length > 0 && (
          <div className={styles.blockers} role="status" data-testid="intake-blockers">
            <strong>Still needed{step > 1 ? ' on step 1' : ''}:</strong>{' '}
            {blockers.join(', ')}
          </div>
        )}

        <footer className={styles.foot}>
          {step > 1 && (
            <Button
              appearance="secondary"
              icon={<ArrowLeft16Regular />}
              onClick={handleBack}
              data-testid="intake-back"
            >
              Back
            </Button>
          )}

          {step === 2 && (
            <Button appearance="subtle" onClick={handleSkip} data-testid="intake-skip">
              Skip this step
            </Button>
          )}

          {step < 3 ? (
            <Button
              appearance="primary"
              disabled={step === 1 && !canAdvance}
              onClick={handleNext}
              icon={<ArrowRight16Regular />}
              iconPosition="after"
              data-testid="intake-next"
            >
              {step === 1 && !canAdvance ? 'Complete required fields' : 'Next'}
            </Button>
          ) : (
            <Button
              appearance="primary"
              disabled={!canAdvance || createMutation.isPending}
              onClick={handleSubmit}
              icon={createMutation.isPending ? <Spinner size="tiny" /> : undefined}
              data-testid="intake-submit"
              title={
                blockers.length
                  ? `Still needed on step 1: ${blockers.join(', ')}`
                  : undefined
              }
            >
              {createMutation.isPending ? 'Submitting…' : 'Submit demand'}
            </Button>
          )}
        </footer>
      </div>

      {showDiscardConfirm && (
        <div
          className={styles.confirmScrim}
          // Stop the click from bubbling to the overlay's attemptClose. The
          // backdrop deliberately does NOT dismiss (modal-style) — only the two
          // buttons + Escape act, so a stray click never discards the form.
          onClick={(e) => e.stopPropagation()}
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="intake-discard-heading"
          data-testid="intake-discard"
        >
          <div className={styles.confirmCard}>
            <h3 id="intake-discard-heading" className={styles.confirmTitle}>
              Discard this demand?
            </h3>
            <p className={styles.confirmBody}>
              You've started filling out this demand. If you close now, everything you've
              entered will be lost.
            </p>
            <div className={styles.confirmActions}>
              <Button
                appearance="secondary"
                onClick={() => setShowDiscardConfirm(false)}
                data-testid="intake-discard-cancel"
              >
                Keep editing
              </Button>
              <Button
                appearance="primary"
                onClick={handleClose}
                data-testid="intake-discard-confirm"
              >
                Discard
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DemandIntakeForm
